import Recorder from 'recorder-core'
// 引入编码格式支持
import 'recorder-core/src/engine/wav'

export class AudioRecorder {
  private static instance: AudioRecorder | null = null;
  private currentRecorder: any = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private frequencyData: Uint8Array | null = null;
  private updateInterval: number | null | NodeJS.Timeout = null;

  private constructor() {}

  public static getInstance(): AudioRecorder {
    if (!this.instance) {
      this.instance = new AudioRecorder();
    }
    return this.instance;
  }

  public onFrequencyData: (frequencyData: Uint8Array) => void = () => {}


  public async recordAudio(stream: MediaStream): Promise<Blob> {
    try {
      if (!Recorder) {
        throw new Error('Recorder库未加载')
      }

      // 创建音频上下文和分析器
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 1024; // 128个频率点，减少数据量
      
      // 缓存频率数据数组，避免重复创建
      this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);

      // 创建音频源
      this.source = this.audioContext.createMediaStreamSource(stream);
      this.source.connect(this.analyser);
    
      
      // 启动低频率的数据更新（大幅降低更新频率）
      this.startFrequencyUpdates();

      // 创建Recorder实例
      this.currentRecorder = Recorder({
        type: 'wav',
        sampleRate: 16000,
        bitRate: 16,
        onProcess: (_: any, __: any, duration: any) => {
          // 只记录录音状态，不进行频繁操作
          if (Math.floor(duration) % 5 === 0 && duration % 1 === 0) {
            console.log('录音中...', duration, 's')
          }
        },
      });

      // 打开麦克风
      return await new Promise((resolve, reject) => {
        this.currentRecorder.open(() => {
          // this.currentRecorder.inputStream = stream;
          this.currentRecorder.start();
          resolve(this.currentRecorder);
        }, (msg: any, isUserNotAllow: any) => {
          this.cleanup();
          const errMsg = isUserNotAllow ? '用户拒绝了录音权限' : msg;
          reject(new Error(`Failed to start recording: ${errMsg}`));
        }, true);
      });
    }
    catch (error) {
      this.cleanup();
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Failed to start recording: ${errorMessage}`);
    }
  }

  /**
   * 启动低频率的频率数据更新
   * 使用定时器而不是音频回调，大幅减少更新频率
   */
  private startFrequencyUpdates(): void {
    const UPDATE_INTERVAL = 300; // 300ms更新一次，降低频率
    
    this.updateInterval = setInterval(() => {
  
      try {
        // 获取频率数据
        this.analyser?.getByteFrequencyData(this.frequencyData as Uint8Array);
      
        
        // 异步更新store，避免阻塞主线程
        requestIdleCallback(() => {
          try {
            this.onFrequencyData(this.frequencyData as Uint8Array);
          } catch (error) {
            console.warn('更新频率数据失败:', error);
          }
        }, { timeout: 100 });
        
      } catch (error) {
        console.warn('获取频率数据失败:', error);
      }
    }, UPDATE_INTERVAL);
  }

  public stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.currentRecorder) {
        console.warn('No active recording to stop');
        reject(new Error('No active recording to stop'));
        return;
      }

      this.cleanup();

      this.currentRecorder.stop((blob: Blob, duration: number) => {
        console.log('录音结束', blob, duration);
        this.resetResources();
        resolve(blob);
      }, (msg: any) => {
        this.resetResources();
        reject(new Error(`Failed to stop recording: ${msg}`));
      }, true);
    });
  }

  private cleanup(): void {
    this.onFrequencyData(new Uint8Array());
    
    // 清理定时器
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    
    // 清理音频资源
    try {
      if (this.source) this.source.disconnect();
      if (this.analyser) this.analyser.disconnect();
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(console.error);
      }
    } catch (error) {
      console.warn('清理音频资源失败:', error);
    }
  }

  private resetResources(): void {
    this.currentRecorder = null;
    this.audioContext = null;
    this.analyser = null;
    this.source = null;
    this.frequencyData = null;
  }
}

// 导出单例实例获取方法
export const getAudioRecorder = (): AudioRecorder => AudioRecorder.getInstance();

export const audioRecorder = getAudioRecorder()
